import React, { useState, useEffect } from 'react';
import { Brain, Users, Database, TrendingUp, ChevronDown, ChevronUp } from 'lucide-react';

const API_URL = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/^http:\/\//i, 'https://')}/api`;

const AICouncilPanel = () => {
  const [stats, setStats] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/ai-council/stats`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (res.ok) setStats(await res.json());
      } catch (e) {
        console.error('Council stats error:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  if (loading) {
    return (
      <div data-testid="ai-council-panel-loading" className="mb-6 bg-[#1E1E1E] border border-white/10 rounded-xl p-4 animate-pulse">
        <div className="h-6 bg-white/5 rounded w-48 mb-3" />
        <div className="h-4 bg-white/5 rounded w-full" />
      </div>
    );
  }

  if (!stats) return null;

  const models = stats.ai_models || [];
  const dataSources = stats.data_sources || {};
  const histDb = stats.historical_db || {};

  return (
    <div data-testid="ai-council-panel" className="mb-6 bg-gradient-to-br from-[#1a2332] to-[#1E1E1E] border border-[#00BFA6]/20 rounded-xl overflow-hidden">
      {/* Header */}
      <button
        data-testid="ai-council-toggle"
        onClick={() => setExpanded(!expanded)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-white/5 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#00BFA6]/10 flex items-center justify-center">
            <Brain className="w-5 h-5 text-[#00BFA6]" />
          </div>
          <div className="text-left">
            <h3 className="text-sm font-bold text-white">AI Council</h3>
            <p className="text-xs text-gray-400">{stats.total_models} KI-Modelle aktiv</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right hidden sm:block">
            <div className="text-xs text-gray-400">Datenpunkte</div>
            <div className="text-sm font-mono font-bold text-[#00BFA6]">{stats.total_data_points}</div>
          </div>
          {expanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </div>
      </button>

      {/* Compact stats bar */}
      <div className="px-4 pb-3 grid grid-cols-3 gap-2">
        <div className="bg-white/5 rounded-lg p-2 text-center">
          <div className="text-xs text-gray-400">Modelle</div>
          <div className="text-base font-bold text-[#00BFA6]">{stats.total_models}</div>
        </div>
        <div className="bg-white/5 rounded-lg p-2 text-center">
          <div className="text-xs text-gray-400">Provider</div>
          <div className="text-base font-bold text-[#7B1FA2]">{stats.providers?.length || 3}</div>
        </div>
        <div className="bg-white/5 rounded-lg p-2 text-center">
          <div className="text-xs text-gray-400">Hist. Daten</div>
          <div className="text-base font-bold text-[#4CAF50]">{histDb.total_records?.toLocaleString() || '0'}</div>
        </div>
      </div>

      {/* Expanded content */}
      {expanded && (
        <div className="border-t border-white/10 px-4 py-4 space-y-4" data-testid="ai-council-details">
          {/* AI Models */}
          <div>
            <h4 className="text-xs font-semibold text-gray-300 uppercase tracking-wider mb-2 flex items-center gap-2">
              <Users className="w-3.5 h-3.5" /> KI-Agenten
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {models.map((m, i) => (
                <div key={i} className="bg-white/5 rounded-lg p-3 border border-white/5">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-white">{m.name}</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#00BFA6]/10 text-[#00BFA6]">{m.provider}</span>
                  </div>
                  <div className="text-xs text-gray-400">{m.role}</div>
                  <div className="text-[10px] text-gray-500 mt-1">{m.specialty}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Communication flow */}
          <div className="bg-white/5 rounded-lg p-3">
            <h4 className="text-xs font-semibold text-gray-300 mb-2 flex items-center gap-2">
              <TrendingUp className="w-3.5 h-3.5" /> Kommunikation
            </h4>
            <div className="flex items-center gap-2 text-xs">
              <span className="px-2 py-1 bg-[#00BFA6]/10 text-[#00BFA6] rounded font-medium">Phase 1</span>
              <span className="text-gray-500">Unabhaengige Analyse</span>
              <span className="text-gray-600">-&gt;</span>
              <span className="px-2 py-1 bg-[#7B1FA2]/10 text-[#7B1FA2] rounded font-medium">Phase 2</span>
              <span className="text-gray-500">Meta-KI Synthese</span>
            </div>
          </div>

          {/* Data Sources */}
          <div>
            <h4 className="text-xs font-semibold text-gray-300 uppercase tracking-wider mb-2 flex items-center gap-2">
              <Database className="w-3.5 h-3.5" /> Datenquellen
            </h4>
            <div className="space-y-1.5">
              {Object.entries(dataSources).map(([key, src]) => (
                <div key={key} className="flex items-center justify-between bg-white/5 rounded px-3 py-2">
                  <div>
                    <span className="text-xs text-white">{src.source}</span>
                    {src.update && <span className="text-[10px] text-gray-500 ml-2">{src.update}</span>}
                  </div>
                  <span className="text-xs font-mono text-[#00BFA6]">{src.data_points}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Historical DB stats */}
          {histDb.total_records > 0 && (
            <div className="bg-gradient-to-r from-[#00BFA6]/5 to-transparent rounded-lg p-3 border border-[#00BFA6]/10">
              <h4 className="text-xs font-semibold text-[#00BFA6] mb-2">Historische Datenbank (30 Jahre)</h4>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div>
                  <div className="text-sm font-bold text-white">{histDb.matches?.toLocaleString()}</div>
                  <div className="text-[10px] text-gray-400">Spiele</div>
                </div>
                <div>
                  <div className="text-sm font-bold text-white">{histDb.teams?.toLocaleString()}</div>
                  <div className="text-[10px] text-gray-400">Teams</div>
                </div>
                <div>
                  <div className="text-sm font-bold text-white">{histDb.standings?.toLocaleString()}</div>
                  <div className="text-[10px] text-gray-400">Tabellen</div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AICouncilPanel;
